import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { putApartmentContent } from '@/api/generated/sdk.gen';
import type { ApartmentContent as ApartmentContentDTO } from '@/api/generated/types.gen';
import { errorMessage } from '@/lib/errors';
import { apartmentContentQuery } from '@/lib/content';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/apartment/OverviewSection';

interface Props {
  apartmentID: string;
  canWrite: boolean;
}

// Build the PUT body: wifi_name/wifi_password are this section's own fields
// (overridden with the edited draft, empty string → omitted so the server
// clears the column — same convention as ApartmentContent's bodyFromDraft,
// tested against '' not .trim() so an intentional whitespace value is sent
// verbatim). Every OTHER field on the row is echoed back verbatim from the
// last-fetched snapshot: the PUT is a full-replace of the same
// `apartment_content` row ApartmentContent's nine sections live in, so
// omitting them here would silently wipe the operator's descriptive content.
function bodyFromDraft(
  data: ApartmentContentDTO | undefined,
  wifiName: string,
  wifiPassword: string,
): Record<string, string | boolean> {
  const body: Record<string, string | boolean> = {};
  if (data) {
    for (const [k, v] of Object.entries(data)) {
      if (k === 'wifi_name' || k === 'wifi_password') continue;
      if (typeof v === 'string' || typeof v === 'boolean') body[k] = v;
    }
  }
  if (wifiName !== '') body.wifi_name = wifiName;
  if (wifiPassword !== '') body.wifi_password = wifiPassword;
  return body;
}

export function AccessOpsSection({ apartmentID, canWrite }: Props) {
  const { data, error, isLoading, isFetching, isSuccess } = useQuery(
    apartmentContentQuery(apartmentID),
  );
  const qc = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [wifiName, setWifiName] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // Editing requires a loaded snapshot — same gate as ApartmentContent, and
  // for the same reason: a full-replace PUT built before the snapshot loads
  // would wipe the descriptive sections this component doesn't own.
  const canEdit = isSuccess && !isFetching && data !== undefined;

  const save = useMutation({
    mutationFn: () =>
      putApartmentContent({
        throwOnError: true,
        path: { id: apartmentID },
        body: bodyFromDraft(data, wifiName, wifiPassword),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apartments', apartmentID, 'content'] });
      setEditing(false);
      setSaveErr(null);
    },
    onError: (e) => setSaveErr(errorMessage(e)),
  });

  const startEdit = () => {
    if (data === undefined) return; // never edit without a loaded snapshot
    setWifiName(data.wifi_name ?? '');
    setWifiPassword(data.wifi_password ?? '');
    setSaveErr(null);
    setEditing(true);
  };

  const loadErr = error ? errorMessage(error) : null;

  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Access &amp; ops</h2>
        {!editing && canWrite && canEdit && (
          <Button type="button" variant="secondary" size="sm" onClick={startEdit}>
            Edit
          </Button>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {loadErr && (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {loadErr}
        </p>
      )}
      {saveErr && (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {saveErr}
        </p>
      )}

      {editing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input label="Wi-Fi name" value={wifiName} onChange={(e) => setWifiName(e.target.value)} />
            <Input
              label="Wi-Fi password"
              value={wifiPassword}
              onChange={(e) => setWifiPassword(e.target.value)}
            />
          </div>
          <div className="mt-5 flex items-center gap-2">
            <Button type="submit" variant="primary" loading={save.isPending} disabled={save.isPending}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <AccessOpsRead data={data} />
      )}
    </section>
  );
}

function AccessOpsRead({ data }: { data: ApartmentContentDTO | undefined }) {
  const hasWifi = (data?.wifi_name ?? '') !== '' || (data?.wifi_password ?? '') !== '';
  if (!hasWifi) {
    return <p className="text-sm text-muted">No access details on file yet.</p>;
  }
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
      <Field label="Wi-Fi name" value={data?.wifi_name || '—'} />
      <Field label="Wi-Fi password" value={data?.wifi_password || '—'} />
    </dl>
  );
}
